import { T } from 'src/components/widgets/helpers/helpers';
import { stringFormat } from 'src/shared/helpers';
import React from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';
import { PropertiesPanelProperty, PropertiesPanelPropertyContent } from './propertiesPanelProperty';
import { PlusIcon, RevealIcon, SwapIcon, TrashIcon } from 'src/components/widgets/icons/scene';
import { useDropdown } from 'src/components/widgets/hooks/useDropdown';
import { PropertiesPanelPicker, PropertiesPanelPickerOption, PropertiesPanelPickerQueryEntry } from './propertiesPanelPicker';
import { ConfirmText, ConfirmTwice, confirmLabel } from '../confirmTwice';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import CommandMenu from '../scene-editors/commandMenu';

// LO SLOT — «in questo posto c'è quell'asset».
//
// È una riga sola, e la stessa riga in tre punti diversi: l'ambiente del World, il materiale di
// un oggetto, la texture di un canale. Il componente non sa di che tipo sia l'asset — l'anteprima
// gliela passa chi lo monta, che sia una sfera, una miniatura o un glifo — ed è il motivo per cui
// è uno solo: cambia il contenuto, non il posto.
//
// I BERSAGLI NON SONO ANNIDATI, ed è la decisione portante. Il difetto classico di questo
// componente è il corpo cliccabile che si mangia i pulsanti dentro di sé, e il rimedio abituale è
// uno `stopPropagation` per pulsante — una riga che se sparisce rompe il componente in silenzio.
// Qui non c'è niente da fermare: sono FRATELLI, e il Tab li percorre tutti.
//
// VUOTO NON È UN CASO A PARTE: è lo stesso posto senza niente dentro. Perde il fondo e prende il
// bordo tratteggiato — il disegno di un posto invece del posto — e dei quattro comandi resta solo
// quello che serve, «mettici qualcosa».
//
// I GESTI SONO COSE DIVERSE, e su quale sta dove non si improvvisa:
//
//     il CORPO — anteprima e nome — mostra DOVE sta l'asset: la sua riga si accende nell'elenco,
//         senza aprirla. Cliccare il nome di una cosa vuol dire «fammela vedere», non «cambiala»;
//     APRI, la freccia, porta dentro l'asset: il suo Inspector (prototipo, «Open»);
//     SOSTITUISCI apre l'elenco. È il gesto che cambia, e ha un bottone suo perché cambiare
//         per sbaglio ciò che si voleva solo guardare è il modo più veloce di rovinare una scena;
//     SVUOTA toglie l'assegnazione: «Clear slot» in fondo all'elenco di Sostituisci, e chiede due volte.
//
// I COMANDI COMPAIONO PASSANDOCI SOPRA (prototipo V3, `.slot-cmds`; utente, 2026-10-19), senza cornice e a
// tratto sottile: a riposo lo slot è un nome e la sua faccia, come un campo.
//
// Vuoto, il corpo resta la scelta, come il «+»: lì non c'è niente da guardare né da aprire.

const PREVIEW_SIZE = '26px';

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

// UN AMBIENTE SI GUARDA PRIMA DI REGOLARLO. Una panoramica riconosciuta da una miniatura di
// trenta pixel non è riconosciuta: dove l'asset è un'immagine larga, l'anteprima grande sta sopra
// lo slot e lo slot resta quello di tutti gli altri.
const Hero = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 96px;
    border: 1px solid ${panel.line};
    border-radius: 8px;
    background: ${panel.field};
    color: ${panel.inkMuted};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    svg {
        width: 28px;
        height: 28px;
    }
`;

// TRENTOTTO PIXEL, come nel prototipo: l'anteprima a 26, alla stessa distanza dal bordo sinistro e da quello
// in alto; il fondo è quello di ogni campo, e il bordo si scurisce passandoci sopra.
const Slot = styled.div<{ $empty: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    box-sizing: border-box;
    min-height: 38px;
    padding: 4px;
    border-radius: 8px;
    border: 1px ${(props) => (props.$empty ? 'dashed' : 'solid')} ${panel.line};
    background: ${(props) => (props.$empty ? 'transparent' : panel.field)};
    transition:
        background 130ms ease,
        border-color 130ms ease;

    &:hover {
        border-color: var(--editor-faint, #676a6e);
    }
`;

const Commands = styled.span`
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 0 0 auto;
    opacity: 0;
    transition: opacity 110ms ease;

    ${Slot}:hover > &,
    ${Slot}:focus-within > &,
    &:has([aria-expanded='true']),
    &:has([data-confirming]) {
        opacity: 1;
    }
`;

const Body = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
`;

const Preview = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${PREVIEW_SIZE};
    height: ${PREVIEW_SIZE};
    border-radius: 6px;
    overflow: hidden;
    color: ${panel.inkMuted};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Text = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const Name = styled.span<{ $empty?: boolean }>`
    overflow: hidden;
    color: ${(props) => (props.$empty ? 'var(--editor-faint, #676a6e)' : panel.ink)};
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Caption = styled.span`
    overflow: hidden;
    color: ${panel.inkMuted};
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// IL ROSSO STA SU UN PULSANTE SOLO, e solo mentre lo si tocca: un cestino già rosso a riposo si
// annuncia distruttivo prima di essere toccato, e in una riga di parametri è un allarme continuo.
// SENZA CORNICE (prototipo, `.slot-cmds .icon-btn`): compaiono passandoci sopra, e un fondo neutro dice
// quale si sta per premere.
const SlotButton = styled.button<{ $danger?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${panel.inkMuted};
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover:not(:disabled),
    &:focus-visible {
        outline: none;
        background: var(--editor-sunken, #e9ebed);
        color: ${(props) => (props.$danger ? 'var(--editor-danger, #c33a31)' : panel.ink)};
    }
`;

// IL «+» DELLO SLOT VUOTO STA DOVE STARÀ LA FACCIA (prototipo, `.slot-add`; utente, 2026-10-20): a sinistra, al
// posto della miniatura, così uno slot vuoto non mostra un'immagine vuota. È un segno, non un secondo bottone:
// la riga intera resta l'unico punto che apre la scelta.
const AddMark = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${PREVIEW_SIZE};
    height: ${PREVIEW_SIZE};
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px ${panel.line};
    color: ${panel.inkMuted};

    svg {
        width: 12px;
        height: 12px;
    }

    ${Body}:hover > & {
        background: ${panel.line};
        color: ${panel.ink};
    }
`;

// L'ELENCO CHE SI APRE DALLO SLOT È QUELLO DEL DESIGN SYSTEM. Lo slot dice da dove si apre e che
// cosa contiene; come si sceglie — la faccia di un asset, la ricerca, il comando che ne crea uno
// nuovo — è una domanda che si ripete in ogni posto dove si sceglie un asset, e la risposta sta
// scritta una volta in PropertiesPanelPicker.
export type ResourceOption = PropertiesPanelPickerOption;

export type PropertiesPanelResourcePropertyProps = {
    id: string;
    /** L'anteprima grande sopra lo slot. Solo dove guardare l'asset serve davvero: un ambiente. */
    hero?: React.ReactNode;
    /** La miniatura dentro lo slot: una sfera, un'immagine, un glifo. La sceglie chi monta. */
    preview?: React.ReactNode;
    /** Il nome dell'asset. Senza, lo slot è vuoto. */
    name?: React.ReactNode;
    /** La riga sotto il nome: formato, misura, gruppo. */
    caption?: React.ReactNode;
    /** Che cosa si legge quando non c'è niente: il gesto che lo riempie, «Choose a material». */
    emptyLabel?: React.ReactNode;
    /** Il corpo: mostra dove questo asset vive — la sua riga si accende nell'elenco, e basta. */
    onReveal?: () => void;
    /** La freccia «Open»: apre l'asset nel suo Inspector. */
    onOpen?: () => void;
    /** Le alternative già disponibili. Con queste, lo slot apre da sé il proprio elenco. */
    options?: ResourceOption[];
    /**
     * UN ELENCO VUOTO SI APRE LO STESSO, E LO DICE (utente, 2026-10-15): senza, lo slot non rispondeva al
     * clic e non si capiva perché. La tendina di prima, aperta, scriveva «No options»: qui la frase la
     * sceglie chi monta lo slot, e dice anche che cosa fare.
     */
    noOptionsLabel?: React.ReactNode;
    /** Una voce in testa all'elenco che nasce dal testo cercato (`PropertiesPanelPicker`). */
    fromQuery?: (query: string) => PropertiesPanelPickerQueryEntry | null;
    /** Quale delle alternative è quella di adesso: nell'elenco si accende. */
    value?: string | number;
    /** Che cosa fare quando si sceglie una voce dell'elenco. */
    onSelect?: (value: string | number) => void;
    /** Il comando in testa all'elenco: «creane uno nuovo». Senza, la riga non c'è. */
    onCreate?: { label: React.ReactNode; onClick: () => void };
    /** Il comando in fondo all'elenco, dopo un filo (`PropertiesPanelPicker`, «Match by name…»). */
    onFooter?: PropertiesPanelPickerQueryEntry;
    /** Che cosa si cerca, scritto nel campo di ricerca. */
    searchPlaceholder?: string;
    /** La tinta della specie, per le voci dell'elenco senza fotografia. */
    optionTint?: string;
    /** Il corpo: apre la scelta. Serve quando l'elenco non è già qui — un caricamento, una libreria. */
    onPick?: () => void;
    /** Sostituisci — lo stesso posto, un altro asset. */
    onReplace?: () => void;
    /** Svuota. C'è solo se c'è qualcosa da togliere. */
    onClear?: () => void;
    disabled?: boolean;
};

export const PropertiesPanelResourceProperty: React.FC<PropertiesPanelResourcePropertyProps> = ({
    id,
    hero,
    preview,
    name,
    caption,
    emptyLabel,
    options,
    value,
    onSelect,
    onCreate,
    onFooter,
    noOptionsLabel,
    fromQuery,
    searchPlaceholder,
    optionTint,
    onReveal,
    onOpen,
    onPick,
    onReplace,
    onClear,
    disabled
}) => {
    const empty = !name;
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    // UN ELENCO VUOTO CON «CREANE UNO» È ANCORA UN ELENCO: in una scena senza materiali il «+» era la
    // sola via per farne nascere uno, e senza voci spariva anche lui.
    const hasList = Boolean(options?.length) || Boolean(onCreate) || (Boolean(options) && (Boolean(noOptionsLabel) || Boolean(fromQuery)));
    const listIsEmpty = !options?.length;

    // Chi ha un elenco lo apre da sé; chi non ce l'ha chiama chi lo monta.
    const choose = (event: React.MouseEvent<HTMLElement>) => {
        if (hasList) openDropdown(event.currentTarget, 'bottom-start');
        else onPick?.();
    };

    const canChoose = hasList || Boolean(onPick);

    // Il corpo porta all'asset; solo dove non c'è dove andare serve a sceglierlo. VUOTO, IL CORPO È
    // L'UNICO PUNTO CHE SCEGLIE (utente, 2026-10-14): accanto c'era anche un «+» che apriva la stessa
    // tendina, due porte per la stessa stanza.
    const openBody = (event: React.MouseEvent<HTMLElement>) => (empty && canChoose ? choose(event) : onReveal ? onReveal() : choose(event));

    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelPropertyContent>
                <Stack>
                    {hero && <Hero aria-hidden='true'>{hero}</Hero>}
                    <Slot id={id} $empty={empty} data-slot>
                        <ZTooltip title={!empty && onReveal ? T._('Show where it lives', 'Admin') : undefined} position='top'>
                            <Body type='button' disabled={disabled || (!canChoose && !onReveal)} onClick={openBody}>
                                {/* VUOTO, UNA RIGA SOLA (prototipo): il «+» a sinistra e «Choose a …» in grigio chiaro. */}
                                {empty && canChoose ? (
                                    <AddMark aria-hidden='true'>
                                        <PlusIcon />
                                    </AddMark>
                                ) : (
                                    preview && <Preview aria-hidden='true'>{preview}</Preview>
                                )}
                                <Text>
                                    <Name $empty={empty}>{empty ? emptyLabel : name}</Name>
                                    {!empty && caption && <Caption>{caption}</Caption>}
                                </Text>
                            </Body>
                        </ZTooltip>
                        {!empty && (
                            <Commands>
                                {onOpen && (
                                    <ZTooltip
                                        title={typeof name === 'string' ? stringFormat(T._('Open {0}', 'Admin'), name) : T._('Open', 'Admin')}
                                        position='top'
                                    >
                                        <SlotButton
                                            type='button'
                                            aria-label={typeof name === 'string' ? stringFormat(T._('Open {0}', 'Admin'), name) : T._('Open', 'Admin')}
                                            disabled={disabled}
                                            onClick={onOpen}
                                        >
                                            <RevealIcon />
                                        </SlotButton>
                                    </ZTooltip>
                                )}
                                {/* REPLACE E CLEAR IN UN MENU SOLO quando si sostituisce caricando un file (prototipo, «Replace or
                                    clear»; decisione 19/10 «Clear nel menu»): un bottone, e sotto le due strade. Con un elenco,
                                    svuotare sta in fondo all'elenco («Clear slot»). */}
                                {onReplace && onClear ? (
                                    <CommandMenu
                                        captions={false}
                                        trigger={
                                            <ZTooltip title={T._('Replace or clear', 'Admin')} position='top'>
                                                <SlotButton type='button' aria-label={T._('Replace or clear', 'Admin')} disabled={disabled}>
                                                    <SwapIcon />
                                                </SlotButton>
                                            </ZTooltip>
                                        }
                                        actions={[
                                            { name: T._('Replace', 'Admin'), tooltip: T._('Replace', 'Admin'), icon: <SwapIcon />, onClick: onReplace },
                                            {
                                                name: T._('Clear slot', 'Admin'),
                                                tooltip: T._('Clear slot', 'Admin'),
                                                icon: <TrashIcon />,
                                                kind: 'danger',
                                                onClick: onClear
                                            }
                                        ]}
                                    />
                                ) : (
                                    <>
                                        {(onReplace || hasList) && (
                                            <ZTooltip title={T._('Replace', 'Admin')} position='top'>
                                                <SlotButton
                                                    type='button'
                                                    aria-label={T._('Replace', 'Admin')}
                                                    aria-expanded={isOpen}
                                                    disabled={disabled}
                                                    onClick={(event) => (onReplace ? onReplace() : choose(event))}
                                                >
                                                    <SwapIcon />
                                                </SlotButton>
                                            </ZTooltip>
                                        )}
                                        {onClear && !hasList && (
                                            <ConfirmTwice onConfirm={onClear}>
                                                {({ armed, ...confirm }) => (
                                                    <ZTooltip title={T._('Clear slot', 'Admin')} position='top'>
                                                        <SlotButton
                                                            type='button'
                                                            $danger
                                                            aria-label={T._('Clear slot', 'Admin')}
                                                            disabled={disabled}
                                                            {...confirm}
                                                        >
                                                            <TrashIcon />
                                                            {armed && <ConfirmText>{confirmLabel()}</ConfirmText>}
                                                        </SlotButton>
                                                    </ZTooltip>
                                                )}
                                            </ConfirmTwice>
                                        )}
                                    </>
                                )}
                            </Commands>
                        )}
                    </Slot>
                    {isOpen && (
                        <Dropdown>
                            <PropertiesPanelPicker
                                options={options ?? []}
                                current={value}
                                tint={optionTint}
                                searchPlaceholder={searchPlaceholder}
                                search={!listIsEmpty || Boolean(fromQuery)}
                                fromQuery={
                                    fromQuery &&
                                    ((query) => {
                                        const entry = fromQuery(query);
                                        return (
                                            entry && {
                                                ...entry,
                                                onClick: () => {
                                                    entry.onClick();
                                                    closeDropdown();
                                                }
                                            }
                                        );
                                    })
                                }
                                emptyLabel={listIsEmpty ? noOptionsLabel : undefined}
                                footer={
                                    onFooter && {
                                        ...onFooter,
                                        onClick: () => {
                                            closeDropdown();
                                            onFooter.onClick();
                                        }
                                    }
                                }
                                onSelect={(chosen) => {
                                    onSelect?.(chosen);
                                    closeDropdown();
                                }}
                                clear={
                                    onClear && !empty && !onReplace
                                        ? {
                                              label: T._('Clear slot', 'Admin'),
                                              onClick: () => {
                                                  closeDropdown();
                                                  onClear();
                                              }
                                          }
                                        : undefined
                                }
                                create={
                                    onCreate && {
                                        label: onCreate.label,
                                        // PRIMA SI CHIUDE L'ELENCO: quello che si apre dopo è una domanda —
                                        // il nome — e va fatta su un pannello solo.
                                        onClick: () => {
                                            closeDropdown();
                                            onCreate.onClick();
                                        }
                                    }
                                }
                            />
                        </Dropdown>
                    )}
                </Stack>
            </PropertiesPanelPropertyContent>
        </PropertiesPanelProperty>
    );
};
